import { useEffect, useState } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Truck, Users, 
  Calendar, BarChart3, Activity, MapPin,
  Download, Search, Wrench
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type DateRange = 'week' | 'current_month' | 'previous_month' | 'q1' | 'q2' | 'q3' | 'q4' | 'year';

interface AnalyticsData {
  // Revenue metrics
  totalRevenue: number;
  revenueChange: number;
  avgRevenuePerLoad: number;
  revenuePerMile: number;
  
  // Load metrics
  totalLoads: number;
  completedLoads: number;
  activeLoads: number;
  cancelledLoads: number;
  
  // Performance metrics
  totalMiles: number;
  avgMilesPerLoad: number;
  avgDeliveryTime: number;
  
  // Driver metrics
  topDrivers: Array<{
    id: string;
    name: string;
    revenue: number;
    loads: number;
    miles: number;
    avgRevenue: number;
    inspectionsPassed: number;
    inspectionsFailed: number;
    accidents: number;
  }>;
  
  // Truck metrics
  topTrucks: Array<{
    id: string;
    unitNumber: string;
    revenue: number;
    loads: number;
    miles: number;
    utilization: number;
    maintenanceCost: number;
    netProfit: number;
    revenuePerMile: number;
  }>;
  
  // Customer metrics
  topCustomers: Array<{
    id: string;
    name: string;
    revenue: number;
    loads: number;
    avgRate: number;
  }>;
  
  // Route metrics
  topRoutes: Array<{
    route: string;
    loads: number;
    revenue: number;
    avgRate: number;
  }>;
  
  // Dispatcher metrics
  topDispatchers: Array<{
    id: string;
    name: string;
    revenue: number;
    loads: number;
    miles: number;
    avgRevenue: number;
    revenuePerMile: number;
    cut: number;
  }>;
  
  // Company metrics
  topCompanies: Array<{
    id: string;
    name: string;
    revenue: number;
    loads: number;
    miles: number;
    avgRevenue: number;
    revenuePerMile: number;
  }>;
  
  // Time-based data
  revenueByDay: Array<{ date: string; revenue: number; loads: number }>;
  loadsByStatus: Array<{ status: string; count: number; percentage: number }>;
  revenueByState: Array<{ state: string; revenue: number }>;
  
  // Maintenance metrics
  totalMaintenanceCost: number;
  totalMaintenanceLogs: number;
  maintenanceThisMonth: number;
  driverMaintenanceCost: number;
  companyMaintenanceCost: number;
}

export function AdvancedAnalyticsView() {
  const [dateRange, setDateRange] = useState<DateRange>('year');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'trucks' | 'drivers' | 'dispatchers' | 'companies'>('overview');
  
  // Search and filter states for individual tabs
  const [truckSearch, setTruckSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [dispatcherSearch, setDispatcherSearch] = useState('');
  const [sortBy, setSortBy] = useState<'revenue' | 'cut' | 'loads' | 'miles' | 'efficiency'>('revenue');
  // Best / Worst toggle, independent per ranking widget (the Top-5 quick cards
  // and the Top-10 tables). Arrays are sorted descending by revenue, so
  // "worst" = the bottom N, lowest first.
  type RankSection =
    | 'drivers' | 'trucks' | 'states' | 'dispatchers'
    | 'tblDrivers' | 'tblTrucks' | 'tblCustomers' | 'tblRoutes';
  const [rankModes, setRankModes] = useState<Record<RankSection, 'best' | 'worst'>>({
    drivers: 'best',
    trucks: 'best',
    states: 'best',
    dispatchers: 'best',
    tblDrivers: 'best',
    tblTrucks: 'best',
    tblCustomers: 'best',
    tblRoutes: 'best',
  });
  const pickN = <T,>(arr: T[], mode: 'best' | 'worst', n: number): T[] =>
    mode === 'best' ? arr.slice(0, n) : arr.slice(-n).reverse();
  const pickFive = <T,>(arr: T[], mode: 'best' | 'worst'): T[] => pickN(arr, mode, 5);
  const rankLabel = (mode: 'best' | 'worst', n = 5) => (mode === 'best' ? `Top ${n}` : `Worst ${n}`);
  const RankToggle = ({ section }: { section: RankSection }) => (
    <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs flex-shrink-0">
      <button
        onClick={() => setRankModes((m) => ({ ...m, [section]: 'best' }))}
        title="Best"
        className={`px-2 py-1 font-semibold transition-colors ${
          rankModes[section] === 'best' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
        }`}
      >
        ▲
      </button>
      <button
        onClick={() => setRankModes((m) => ({ ...m, [section]: 'worst' }))}
        title="Worst"
        className={`px-2 py-1 font-semibold transition-colors ${
          rankModes[section] === 'worst' ? 'bg-red-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
        }`}
      >
        ▼
      </button>
    </div>
  );

  useEffect(() => {
    loadAnalytics();

    // Auto-refresh every 5 minutes. Silent refresh: numbers update in place,
    // the page does NOT show the loading spinner or reset.
    const interval = setInterval(() => {
      loadAnalytics(true);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dateRange]);

  async function loadAnalytics(silent = false) {
    if (!silent) setLoading(true);
    try {
      const dateFilter = getDateFilter(dateRange);
      const endDateFilter = getEndDateFilter(dateRange);
      const previousDateFilter = getPreviousDateFilter(dateRange);

      // Fetch all data in parallel
      const [
        currentRevenue,
        previousRevenue,
        currentLoads,
        drivers,
        trucks,
        customers,
        companies,
        activeDispatches,
        maintenanceLogs,
        removedLoadRows,
        dispatchMiles,
        previousLoads
      ] = await Promise.all([
        // Current period revenue
        endDateFilter
          ? supabase
              .from('revenue_records')
              .select('*')
              .gte('completed_date', dateFilter)
              .lt('completed_date', endDateFilter)
          : supabase
              .from('revenue_records')
              .select('*')
              .gte('completed_date', dateFilter),
        // Previous period revenue
        supabase
          .from('revenue_records')
          .select('*')
          .gte('completed_date', previousDateFilter)
          .lt('completed_date', dateFilter),
        // Current period loads — removed loads excluded, end-bounded to the range
        endDateFilter
          ? supabase
              .from('loads')
              .select('*, load_stops(city, state, stop_type, stop_order)')
              .neq('status', 'removed')
              .gte('created_at', dateFilter)
              .lt('created_at', endDateFilter)
          : supabase
              .from('loads')
              .select('*, load_stops(city, state, stop_type, stop_order)')
              .neq('status', 'removed')
              .gte('created_at', dateFilter),
        // Drivers
        supabase
          .from('drivers')
          .select('id, first_name, last_name, is_active, company_id'),
        // Trucks
        supabase
          .from('trucks')
          .select('id, unit_number, status, company_id'),
        // Customers
        supabase
          .from('customers')
          .select('id, name'),
        // Companies
        supabase
          .from('companies')
          .select('id, name, code')
          .eq('is_active', true)
          .order('name'),
        // Active dispatches
        supabase
          .from('dispatches')
          .select('id')
          .eq('status', 'active'),
        // Maintenance logs for cost tracking - get ALL maintenance logs (not just current month)
        supabase
          .from('maintenance_logs')
          .select('truck_id, cost, service_date, company_cost_amount'),
        // Removed-load ids — used to drop their surviving revenue_records from
        // every metric (soft-removed loads keep their revenue rows).
        supabase
          .from('loads')
          .select('id')
          .or('status.eq.removed,load_number.ilike.%REMOVED%'),
        // Empty miles + driver pay/type live on dispatches; joined to loads
        // client-side for the miles metrics and the dispatcher "cut" (company
        // margin = rate - driver pay, only for non-company drivers).
        // Paged: this is the whole table, so an unbounded read would silently
        // drop the newest dispatches and undercount miles and margin.
        fetchAllRows<any>(() => supabase
          .from('dispatches')
          .select('id, load_id, empty_miles, driver_pay, drivers(driver_type)')
        ).then(data => ({ data })),
        // Previous-period loads (delivered/archived, non-removed) so the
        // revenue trend compares like-for-like with the current period.
        supabase
          .from('loads')
          .select('id, total_rate, status, load_number')
          .in('status', ['delivered', 'archived'])
          .gte('created_at', previousDateFilter)
          .lt('created_at', dateFilter)
      ]);

      // Log data for debugging
      console.log('📊 Analytics Data Loaded:', {
        dateRange,
        dateFilter,
        endDateFilter,
        revenueRecords: currentRevenue.data?.length || 0,
        loads: currentLoads.data?.length || 0,
        drivers: drivers.data?.length || 0,
        trucks: trucks.data?.length || 0,
        maintenanceLogs: maintenanceLogs.data?.length || 0,
        activeDispatches: activeDispatches.data?.length || 0
      });

      // Check for errors
      if (currentRevenue.error) console.error('❌ Revenue error:', currentRevenue.error);
      if (drivers.error) console.error('❌ Drivers error:', drivers.error);
      if (trucks.error) console.error('❌ Trucks error:', trucks.error);
      if (maintenanceLogs.error) console.error('❌ Maintenance error:', maintenanceLogs.error);
      if (activeDispatches.error) console.error('❌ Dispatches error:', activeDispatches.error);
      
      // Log sample revenue record if available
      if (currentRevenue.data && currentRevenue.data.length > 0) {
        console.log('📝 Sample revenue record:', currentRevenue.data[0]);
      }
      
      console.log('🚛 All trucks included in analytics:', trucks.data?.length || 0);
      console.log('👤 All drivers included in analytics:', drivers.data?.length || 0);
      console.log('📋 All dispatches in system:', currentLoads.data?.length || 0);
      console.log('⚡ Active dispatches:', activeDispatches.data?.length || 0);
      
      // Show sample dispatch data to understand status
      if (currentLoads.data && currentLoads.data.length > 0) {
        const deliveredDispatches = currentLoads.data.filter(d => d.status === 'delivered' || d.status === 'archived');
        console.log('✅ Delivered/Archived loads found:', deliveredDispatches.length);
      }

      // Removed loads (soft-removed rows keep their revenue_records; drop both)
      const removedLoadIds = new Set(
        (removedLoadRows.data || []).map((r: { id: string }) => r.id)
      );

      // Per-load dispatch info (from dispatches): empty miles, driver pay, and
      // driver type — used by loadMiles() and cutForLoad().
      const dispatchByLoad = new Map<string, { emptyMiles: number; driverPay: number; driverType: string }>();
      (dispatchMiles.data || []).forEach((d: any) => {
        if (d.load_id) {
          const prev = dispatchByLoad.get(d.load_id);
          dispatchByLoad.set(d.load_id, {
            emptyMiles: (prev?.emptyMiles || 0) + Number(d.empty_miles || 0),
            driverPay: (prev?.driverPay || 0) + Number(d.driver_pay || 0),
            driverType: d.drivers?.driver_type || prev?.driverType || '',
          });
        }
      });
      const loadMiles = (loadId: string | null | undefined, baseMiles: number | null | undefined) =>
        Number(baseMiles || 0) + (loadId ? dispatchByLoad.get(loadId)?.emptyMiles || 0 : 0);
      // Dispatcher cut = company margin (rate - driver pay) on owner-operator /
      // rent / buy loads; company drivers contribute 0 (matches dispatchers-gross).
      const cutForLoad = (loadId: string | null | undefined, gross: number) => {
        if (!loadId) return 0;
        const info = dispatchByLoad.get(loadId);
        if (!info || info.driverType === 'company_driver') return 0;
        return gross - info.driverPay;
      };

      // Create filtered data for overview metrics and ALL analyses - RESPECT DATE RANGE FILTER
      const filteredRevenue = currentRevenue.data?.filter(record => {
        if (removedLoadIds.has(record.load_id)) return false; // exclude removed loads' revenue
        if (endDateFilter) {
          const recordDate = new Date(record.completed_date);
          const filterDate = new Date(dateFilter);
          const endFilterDate = new Date(endDateFilter);
          return recordDate >= filterDate && recordDate < endFilterDate;
        } else {
          const recordDate = new Date(record.completed_date);
          const filterDate = new Date(dateFilter);
          return recordDate >= filterDate;
        }
      }) || [];

      // Statuses are MIXED CASE in the data (DELIVERED/ARCHIVED/dispatched/
      // booked/delivered). A load counts toward revenue unless it's removed or
      // cancelled — matching "total revenue from loads, exclude removed loads".
      const isRevenueStatus = (status: string | null | undefined) => {
        const s = (status || '').toLowerCase();
        return s !== 'removed' && s !== 'cancelled';
      };

      const filteredLoads = currentLoads.data?.filter(load => {
        if (load.load_number?.includes('_REMOVED_')) return false; // legacy removed naming
        if (!isRevenueStatus(load.status)) return false;
        const loadDate = new Date(load.created_at);
        const filterDate = new Date(dateFilter);
        if (endDateFilter && loadDate >= new Date(endDateFilter)) return false;
        return loadDate >= filterDate;
      }) || [];

      // Loads that already have a revenue record must not be counted twice —
      // the record is the source of truth; dedupedLoads covers the rest.
      const recordLoadIds = new Set(filteredRevenue.map(r => r.load_id).filter(Boolean));
      const dedupedLoads = filteredLoads.filter(load => !recordLoadIds.has(load.id));

      // Calculate revenue metrics - USE FILTERED DATA to respect date range
      const revenueFromRecords = filteredRevenue.reduce((sum, r) => sum + Number(r.total_revenue), 0);
      const revenueFromArchivedLoads = dedupedLoads
        .reduce((sum, load) => sum + Number(load.total_rate || 0), 0);

      const totalRevenue = revenueFromRecords + revenueFromArchivedLoads;

      // Previous period uses the same rules: drop removed loads' records, add
      // delivered/archived loads that have no record (deduped), so the trend
      // compares like-for-like.
      const prevRecords = (previousRevenue.data || []).filter(r => !removedLoadIds.has(r.load_id));
      const prevRecordLoadIds = new Set(prevRecords.map(r => r.load_id).filter(Boolean));
      const prevLoadsRevenue = (previousLoads.data || [])
        .filter(l => !prevRecordLoadIds.has(l.id) && !removedLoadIds.has(l.id) && !l.load_number?.includes('_REMOVED_'))
        .reduce((sum, l) => sum + Number(l.total_rate || 0), 0);
      const previousTotalRevenue =
        prevRecords.reduce((sum, r) => sum + Number(r.total_revenue), 0) + prevLoadsRevenue;
      const revenueChange = previousTotalRevenue > 0
        ? ((totalRevenue - previousTotalRevenue) / previousTotalRevenue) * 100
        : 0;

      const completedLoads = filteredRevenue.length + dedupedLoads.length;

      // Miles always include loaded + empty miles (empty miles joined from dispatches)
      const milesFromRecords = filteredRevenue.reduce((sum, r) => sum + loadMiles(r.load_id, r.total_miles), 0);
      const milesFromArchivedLoads = dedupedLoads
        .reduce((sum, load) => sum + loadMiles(load.id, load.total_miles), 0);

      const totalMiles = milesFromRecords + milesFromArchivedLoads;
      
      const avgRevenuePerLoad = completedLoads > 0 ? totalRevenue / completedLoads : 0;
      const revenuePerMile = totalMiles > 0 ? totalRevenue / totalMiles : 0;
      const avgMilesPerLoad = completedLoads > 0 ? totalMiles / completedLoads : 0;

      // Calculate revenue status breakdown (Active vs Completed)
      const activeDispatchesCount = activeDispatches.data?.length || 0;
      const completedLoadsCount = completedLoads;
      const totalDispatchesCount = activeDispatchesCount + completedLoadsCount;
      
      const loadsByStatus = [
        {
          status: 'Completed',
          count: completedLoadsCount,
          percentage: totalDispatchesCount > 0 ? (completedLoadsCount / totalDispatchesCount) * 100 : 0
        },
        {
          status: 'Active',
          count: activeDispatchesCount,
          percentage: totalDispatchesCount > 0 ? (activeDispatchesCount / totalDispatchesCount) * 100 : 0
        }
      ].filter(item => item.count > 0); // Only show statuses with data

      const totalLoadsCount = currentLoads.data?.length || 0;

      // Top Drivers Analysis - Show ALL drivers, not just ones with revenue
      const driverRevenue = new Map<string, { name: string; revenue: number; loads: number; miles: number }>();
      
      // Initialize all drivers with zero revenue/loads/miles
      drivers.data?.forEach(driver => {
        const driverName = `${driver.first_name} ${driver.last_name}`.trim() || 'Unknown';
        driverRevenue.set(driver.id, {
          name: driverName,
          revenue: 0,
          loads: 0,
          miles: 0
        });
      });
      
      // Add revenue data for drivers that have completed deliveries (DELIVERED or ARCHIVED)
      // Exclude loads with 0 miles to get accurate rate per mile calculations
      filteredRevenue.forEach(record => {
        if (record.driver_id && (record.total_miles || 0) > 0) {
          const current = driverRevenue.get(record.driver_id);
          if (current) {
            driverRevenue.set(record.driver_id, {
              ...current,
              revenue: current.revenue + Number(record.total_revenue),
              loads: current.loads + 1,
              miles: current.miles + loadMiles(record.load_id, record.total_miles)
            });
          }
        }
      });

      // Also add data directly from loads table for ARCHIVED and DELIVERED loads without revenue records
      // Exclude loads with 0 miles to get accurate rate per mile calculations
      dedupedLoads.forEach(load => {
        if (load.driver_id && (load.total_miles || 0) > 0) {
          const current = driverRevenue.get(load.driver_id);
          if (current) {
            driverRevenue.set(load.driver_id, {
              ...current,
              revenue: current.revenue + Number(load.total_rate || 0),
              loads: current.loads + 1,
              miles: current.miles + loadMiles(load.id, load.total_miles)
            });
          }
        }
      });

      // Fetch inspection and accident counts for drivers
      const driverIds = Array.from(driverRevenue.keys());
      let inspectionsQuery = supabase
        .from('inspections')
        .select('driver_id, passed')
        .in('driver_id', driverIds)
        .gte('date', dateFilter);
      let accidentsQuery = supabase
        .from('accidents')
        .select('driver_id')
        .in('driver_id', driverIds)
        .gte('date', dateFilter);
      if (endDateFilter) {
        inspectionsQuery = inspectionsQuery.lt('date', endDateFilter);
        accidentsQuery = accidentsQuery.lt('date', endDateFilter);
      }
      const [inspectionsData, accidentsData] = await Promise.all([inspectionsQuery, accidentsQuery]);

      // Count passed/failed inspections and accidents per driver
      const inspectionsPassedCounts = new Map<string, number>();
      const inspectionsFailedCounts = new Map<string, number>();
      const accidentCounts = new Map<string, number>();
      
      inspectionsData.data?.forEach(insp => {
        if (insp.passed) {
          inspectionsPassedCounts.set(insp.driver_id, (inspectionsPassedCounts.get(insp.driver_id) || 0) + 1);
        } else {
          inspectionsFailedCounts.set(insp.driver_id, (inspectionsFailedCounts.get(insp.driver_id) || 0) + 1);
        }
      });
      
      accidentsData.data?.forEach(acc => {
        accidentCounts.set(acc.driver_id, (accidentCounts.get(acc.driver_id) || 0) + 1);
      });

      const topDrivers = Array.from(driverRevenue.entries())
        .map(([id, data]) => ({
          id,
          ...data,
          avgRevenue: data.loads > 0 ? data.revenue / data.loads : 0,
          inspectionsPassed: inspectionsPassedCounts.get(id) || 0,
          inspectionsFailed: inspectionsFailedCounts.get(id) || 0,
          accidents: accidentCounts.get(id) || 0
        }))
        .filter(driver => 
          driver.revenue > 0 || 
          driver.loads > 0 || 
          driver.miles > 0
        ) // Only show drivers with revenue-generating activity
        .sort((a, b) => b.revenue - a.revenue);

      // Calculate maintenance costs per truck
      const truckMaintenanceCosts = new Map<string, number>();
      maintenanceLogs.data?.forEach(log => {
        if (log.truck_id && log.cost) {
          const current = truckMaintenanceCosts.get(log.truck_id) || 0;
          truckMaintenanceCosts.set(log.truck_id, current + Number(log.cost));
        }
      });

      console.log('🔧 Trucks with maintenance costs:', truckMaintenanceCosts.size);
      console.log('📝 Maintenance logs found:', maintenanceLogs.data?.length || 0);
      if (maintenanceLogs.data && maintenanceLogs.data.length > 0) {
        console.log('🔧 Sample maintenance log:', maintenanceLogs.data[0]);
        console.log('🔧 All maintenance costs:', Array.from(truckMaintenanceCosts.entries()));
      }

      // Top Trucks Analysis - Show ALL trucks, not just ones with revenue
      const truckRevenue = new Map<string, { unitNumber: string; revenue: number; loads: number; miles: number }>();
      
      // Initialize all trucks with zero revenue/loads/miles
      trucks.data?.forEach(truck => {
        truckRevenue.set(truck.id, {
          unitNumber: truck.unit_number || 'Unknown',
          revenue: 0,
          loads: 0,
          miles: 0
        });
      });
      
      // Add revenue data for trucks that have completed deliveries (DELIVERED or ARCHIVED)
      filteredRevenue.forEach(record => {
        if (record.truck_id) {
          const current = truckRevenue.get(record.truck_id);
          if (current) {
            truckRevenue.set(record.truck_id, {
              ...current,
              revenue: current.revenue + Number(record.total_revenue),
              loads: current.loads + 1,
              miles: current.miles + loadMiles(record.load_id, record.total_miles)
            });
          }
        }
      });

      // Also add data directly from loads table for ARCHIVED and DELIVERED loads without revenue records
      dedupedLoads.forEach(load => {
        if (load.truck_id) {
          const current = truckRevenue.get(load.truck_id);
          if (current) {
            truckRevenue.set(load.truck_id, {
              ...current,
              revenue: current.revenue + Number(load.total_rate || 0),
              loads: current.loads + 1,
              miles: current.miles + loadMiles(load.id, load.total_miles)
            });
          }
        }
      });

      const topTrucks = Array.from(truckRevenue.entries())
        .map(([id, data]) => {
          const maintenanceCost = truckMaintenanceCosts.get(id) || 0;
          const netProfit = data.revenue - maintenanceCost;
          return {
            id,
            ...data,
            utilization: data.loads,
            maintenanceCost,
            netProfit,
            revenuePerMile: data.miles > 0 ? data.revenue / data.miles : 0
          };
        })
        .filter(truck => truck.revenue > 0 || truck.maintenanceCost > 0) // Only show trucks with revenue OR maintenance
        .sort((a, b) => b.revenue - a.revenue);

      // Top Customers Analysis - include both revenue records and archived/delivered loads
      const customerRevenue = new Map<string, { name: string; revenue: number; loads: number }>();
      
      // Add customers from revenue records
      filteredRevenue.forEach(record => {
        if (record.customer_id) {
          const customer = customers.data?.find(c => c.id === record.customer_id);
          const customerName = customer?.name || 'Unknown';
          const current = customerRevenue.get(record.customer_id) || { name: customerName, revenue: 0, loads: 0 };
          customerRevenue.set(record.customer_id, {
            name: customerName,
            revenue: current.revenue + Number(record.total_revenue),
            loads: current.loads + 1
          });
        }
      });
      
      // Add customers from archived/delivered loads
      dedupedLoads.forEach(load => {
        if (load.customer_id) {
          const customer = customers.data?.find(c => c.id === load.customer_id);
          const customerName = customer?.name || 'Unknown Customer';
          const current = customerRevenue.get(load.customer_id) || { name: customerName, revenue: 0, loads: 0 };
          customerRevenue.set(load.customer_id, {
            name: customerName,
            revenue: current.revenue + Number(load.total_rate || 0),
            loads: current.loads + 1
          });
        }
      });

      const topCustomers = Array.from(customerRevenue.entries())
        .map(([id, data]) => ({
          id,
          ...data,
          avgRate: data.loads > 0 ? data.revenue / data.loads : 0
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Top Routes Analysis - include both revenue records and archived/delivered loads
      const routeRevenue = new Map<string, { loads: number; revenue: number }>();
      
      // Add routes from revenue records - get actual locations from original load
      filteredRevenue.forEach(record => {
        // Find the original load to get actual pickup/delivery locations
        const originalLoad = currentLoads.data?.find(load => load.id === record.load_id);
        
        let pickupLocation = 'Unknown';
        let deliveryLocation = 'Unknown';
        
        if (originalLoad) {
          // Get pickup location from original load
          if (originalLoad.pickup_location) {
            pickupLocation = originalLoad.pickup_location;
          } else if (originalLoad.pickup_city) {
            pickupLocation = originalLoad.pickup_city;
          } else if (record.pickup_city) {
            pickupLocation = `${record.pickup_city}, ${record.pickup_state || ''}`;
          }
          
          // Get delivery location from original load
          if (originalLoad.delivery_location) {
            deliveryLocation = originalLoad.delivery_location;
          } else if (originalLoad.delivery_city) {
            deliveryLocation = originalLoad.delivery_city;
          } else if (record.delivery_city) {
            deliveryLocation = `${record.delivery_city}, ${record.delivery_state || ''}`;
          }
        } else {
          // Fallback to revenue record data
          pickupLocation = `${record.pickup_city || 'Unknown'}, ${record.pickup_state || ''}`;
          deliveryLocation = `${record.delivery_city || 'Unknown'}, ${record.delivery_state || ''}`;
        }
        
        const route = `${pickupLocation} → ${deliveryLocation}`;
        const current = routeRevenue.get(route) || { loads: 0, revenue: 0 };
        routeRevenue.set(route, {
          loads: current.loads + 1,
          revenue: current.revenue + Number(record.total_revenue)
        });
      });
      
      // Add routes from archived/delivered loads with actual locations
      dedupedLoads.forEach(load => {
        if (isRevenueStatus(load.status)) {
          let pickupLocation = 'Unknown';
          let deliveryLocation = 'Unknown';
          
          // Get locations from load_stops if available
          if (load.load_stops && Array.isArray(load.load_stops) && load.load_stops.length > 0) {
            // Find shipper (pickup) stop
            const shipperStop = load.load_stops.find((stop: any) => stop.stop_type === 'shipper');
            if (shipperStop && shipperStop.city) {
              pickupLocation = shipperStop.state ? `${shipperStop.city}, ${shipperStop.state}` : shipperStop.city;
            }
            
            // Find consignee (delivery) stop
            const consigneeStop = load.load_stops.find((stop: any) => stop.stop_type === 'consignee');
            if (consigneeStop && consigneeStop.city) {
              deliveryLocation = consigneeStop.state ? `${consigneeStop.city}, ${consigneeStop.state}` : consigneeStop.city;
            }
          } else {
            // Fallback to old fields if load_stops not available
            if (load.pickup_location) {
              pickupLocation = load.pickup_location;
            } else if (load.pickup_city) {
              pickupLocation = load.pickup_state ? `${load.pickup_city}, ${load.pickup_state}` : load.pickup_city;
            }
            
            if (load.delivery_location) {
              deliveryLocation = load.delivery_location;
            } else if (load.delivery_city) {
              deliveryLocation = load.delivery_state ? `${load.delivery_city}, ${load.delivery_state}` : load.delivery_city;
            }
          }
          
          const route = `${pickupLocation} → ${deliveryLocation}`;
          const current = routeRevenue.get(route) || { loads: 0, revenue: 0 };
          routeRevenue.set(route, {
            loads: current.loads + 1,
            revenue: current.revenue + Number(load.total_rate || 0)
          });
        }
      });

      const topRoutes = Array.from(routeRevenue.entries())
        .map(([route, data]) => ({
          route,
          ...data,
          avgRate: data.loads > 0 ? data.revenue / data.loads : 0
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Revenue by Pickup State - include both revenue records and archived/delivered loads
      const stateRevenue = new Map<string, number>();
      
      // Add revenue from revenue records - get state from original load if revenue record missing state
      filteredRevenue.forEach(record => {
        let state = record.pickup_state || 'Unknown';
        
        // If revenue record doesn't have state, try to find it in the original load
        if (state === 'Unknown') {
          const originalLoad = currentLoads.data?.find(load => load.id === record.load_id);
          if (originalLoad) {
            if (originalLoad.pickup_state) {
              state = originalLoad.pickup_state;
            } else if (originalLoad.pickup_location) {
              const locationParts = originalLoad.pickup_location.split(',');
              if (locationParts.length >= 2) {
                state = locationParts[locationParts.length - 1].trim();
              }
            }
          }
        }
        
        stateRevenue.set(state, (stateRevenue.get(state) || 0) + Number(record.total_revenue));
      });
      
      // Add revenue from archived/delivered loads with actual pickup locations
      dedupedLoads.forEach(load => {
        if (isRevenueStatus(load.status)) {
          let state = 'Unknown';
          
          // Get state from load_stops if available
          if (load.load_stops && Array.isArray(load.load_stops) && load.load_stops.length > 0) {
            // Find shipper (pickup) stop
            const shipperStop = load.load_stops.find((stop: any) => stop.stop_type === 'shipper');
            if (shipperStop && shipperStop.state) {
              state = shipperStop.state;
            }
          } else {
            // Fallback to old fields if load_stops not available
            if (load.pickup_state) {
              state = load.pickup_state;
            } else if (load.pickup_location) {
              // Try to extract state from pickup_location string (format: "City, STATE")
              const locationParts = load.pickup_location.split(',');
              if (locationParts.length >= 2) {
                state = locationParts[locationParts.length - 1].trim();
              }
            } else if (load.pickup_city && load.pickup_city.includes(',')) {
              // If pickup_city has "City, STATE" format
              const cityParts = load.pickup_city.split(',');
              if (cityParts.length >= 2) {
                state = cityParts[cityParts.length - 1].trim();
              }
            }
          }
          
          stateRevenue.set(state, (stateRevenue.get(state) || 0) + Number(load.total_rate || 0));
        }
      });

      const revenueByState = Array.from(stateRevenue.entries())
        .map(([state, revenue]) => ({ state, revenue }))
        .sort((a, b) => b.revenue - a.revenue);

      // Top Dispatchers Analysis (tracking loads they booked)
      // We need to get the load creator from the loads table
      const loadIds = filteredRevenue.map(r => r.load_id).filter(Boolean);
      let loadsWithCreator: Array<{ id: string; created_by?: string; user_id?: string }> = [];
      if (loadIds.length > 0) {
        const { data } = await supabase
          .from('loads')
          .select('id, created_by, user_id')
          .in('id', loadIds);
        loadsWithCreator = data || [];
      }

      // Create a map of load_id to creator (try created_by first, then user_id, then fall back to revenue_records.created_by)
      const loadCreatorMap = new Map(
        loadsWithCreator?.map(l => [l.id, l.created_by || l.user_id]) || []
      );

      const dispatcherRevenue = new Map<string, { name: string; revenue: number; loads: number; miles: number; cut: number }>();

      // Add revenue data from revenue records (DELIVERED loads)
      filteredRevenue.forEach(record => {
        // Get who created the LOAD - try load creator first, fall back to revenue record creator
        const dispatcherId = loadCreatorMap.get(record.load_id) || record.created_by;
        if (dispatcherId) {
          const current = dispatcherRevenue.get(dispatcherId) || { name: 'Unknown', revenue: 0, loads: 0, miles: 0, cut: 0 };
          dispatcherRevenue.set(dispatcherId, {
            name: current.name, // Will be updated when we fetch user profiles
            revenue: current.revenue + Number(record.total_revenue),
            loads: current.loads + 1,
            miles: current.miles + loadMiles(record.load_id, record.total_miles),
            cut: current.cut + cutForLoad(record.load_id, Number(record.total_revenue))
          });
        }
      });

      // Also add data directly from loads table for ARCHIVED and DELIVERED loads without revenue records
      dedupedLoads.forEach(load => {
        if (isRevenueStatus(load.status) && load.created_by) {
          const current = dispatcherRevenue.get(load.created_by) || { name: 'Unknown', revenue: 0, loads: 0, miles: 0, cut: 0 };
          dispatcherRevenue.set(load.created_by, {
            name: current.name, // Will be updated when we fetch user profiles
            revenue: current.revenue + Number(load.total_rate || 0),
            loads: current.loads + 1,
            miles: current.miles + loadMiles(load.id, load.total_miles),
            cut: current.cut + cutForLoad(load.id, Number(load.total_rate || 0))
          });
        }
      });

      // Fetch user profiles for dispatchers (but preserve data even if profile is deleted)
      const dispatcherIds = Array.from(dispatcherRevenue.keys());
      if (dispatcherIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', dispatcherIds);

        // Update dispatcher names for found profiles
        profiles?.forEach(profile => {
          const current = dispatcherRevenue.get(profile.id);
          if (current) {
            dispatcherRevenue.set(profile.id, {
              ...current,
              name: profile.full_name || profile.email || 'Unknown'
            });
          }
        });
        
        // For dispatchers without profiles (fired/deleted), try to get name from loads table
        dispatcherIds.forEach(dispatcherId => {
          const current = dispatcherRevenue.get(dispatcherId);
          if (current && current.name === 'Unknown') {
            // Find a load created by this dispatcher to get their email/name
            const loadByDispatcher = currentLoads.data?.find(load => 
              isRevenueStatus(load.status) &&
              load.created_by === dispatcherId
            );
            if (loadByDispatcher) {
              // Use email as fallback name if profile is gone
              const fallbackName = loadByDispatcher.created_by === dispatcherId ? 
                loadByDispatcher.user_id || `Dispatcher ${dispatcherId.slice(0, 8)}` : 
                `Former Dispatcher ${dispatcherId.slice(0, 8)}`;
              
              dispatcherRevenue.set(dispatcherId, {
                ...current,
                name: fallbackName
              });
            }
          }
        });
      }

      const topDispatchers = Array.from(dispatcherRevenue.entries())
        .map(([id, data]) => ({
          id,
          ...data,
          avgRevenue: data.loads > 0 ? data.revenue / data.loads : 0,
          revenuePerMile: data.miles > 0 ? data.revenue / data.miles : 0
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Company Performance Analysis - based on carrier field
      const companyRevenue = new Map<string, { name: string; revenue: number; loads: number; miles: number }>();
      
      // Add data from delivered/archived loads
      dedupedLoads.forEach(load => {
        if (isRevenueStatus(load.status) && load.carrier) {
          const current = companyRevenue.get(load.carrier) || { name: load.carrier, revenue: 0, loads: 0, miles: 0 };
          companyRevenue.set(load.carrier, {
            name: load.carrier,
            revenue: current.revenue + Number(load.total_rate || 0),
            loads: current.loads + 1,
            miles: current.miles + loadMiles(load.id, load.total_miles)
          });
        }
      });

      const topCompanies = Array.from(companyRevenue.entries())
        .map(([carrier, data]) => ({
          id: carrier,
          ...data,
          avgRevenue: data.loads > 0 ? data.revenue / data.loads : 0,
          revenuePerMile: data.miles > 0 ? data.revenue / data.miles : 0
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Revenue by Day (last 30 days or based on range) — merge revenue records
      // with deduped delivered/archived loads so the chart matches totalRevenue.
      const revenueEvents = [
        ...filteredRevenue.map(r => ({ completed_date: r.completed_date, total_revenue: r.total_revenue })),
        ...dedupedLoads.map(l => ({ completed_date: l.created_at, total_revenue: l.total_rate || 0 })),
      ];
      const revenueByDay = generateRevenueByDay(revenueEvents, dateRange);

      // Calculate maintenance metrics - RESPECT DATE RANGE FILTER
      const filteredMaintenanceLogs = maintenanceLogs.data?.filter(log => {
        const logDate = new Date(log.service_date);
        const filterDate = new Date(dateFilter);
        
        switch (dateRange) {
          case 'week':
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return logDate >= weekAgo;
          case 'current_month':
            const now = new Date();
            return logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
          case 'previous_month':
            const lastMonth = new Date();
            lastMonth.setMonth(lastMonth.getMonth() - 1);
            return logDate.getMonth() === lastMonth.getMonth() && logDate.getFullYear() === lastMonth.getFullYear();
          case 'q1':
            const year = logDate.getFullYear();
            return logDate.getMonth() >= 0 && logDate.getMonth() <= 2 && logDate.getFullYear() === year;
          case 'q2':
            const q2Year = logDate.getFullYear();
            return logDate.getMonth() >= 3 && logDate.getMonth() <= 5 && logDate.getFullYear() === q2Year;
          case 'q3':
            const q3Year = logDate.getFullYear();
            return logDate.getMonth() >= 6 && logDate.getMonth() <= 8 && logDate.getFullYear() === q3Year;
          case 'q4':
            const q4Year = logDate.getFullYear();
            return logDate.getMonth() >= 9 && logDate.getMonth() <= 11 && logDate.getFullYear() === q4Year;
          case 'year':
            const currentYear = new Date().getFullYear();
            return logDate.getFullYear() === currentYear;
          default:
            return logDate >= filterDate;
        }
      }) || [];
      
      const totalMaintenanceCost = filteredMaintenanceLogs.reduce((sum, log) => sum + Number(log.cost || 0), 0);
      const totalMaintenanceLogs = filteredMaintenanceLogs.length;
      
      // Calculate "This Month" specifically (current month, regardless of filter)
      const now = new Date();
      const maintenanceThisMonth = maintenanceLogs.data
        ?.filter(log => {
          const logDate = new Date(log.service_date);
          return logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
        })
        ?.reduce((sum, log) => sum + Number(log.cost || 0), 0) || 0;
      
      const driverMaintenanceCost = filteredMaintenanceLogs
        .reduce((sum, log) => sum + (Number(log.cost || 0) - Number(log.company_cost_amount || 0)), 0) || 0;
      
      const companyMaintenanceCost = filteredMaintenanceLogs
        .reduce((sum, log) => sum + Number(log.company_cost_amount || 0), 0) || 0;

      setAnalytics({
        totalRevenue,
        revenueChange,
        avgRevenuePerLoad,
        revenuePerMile,
        totalLoads: totalLoadsCount,
        completedLoads,
        activeLoads: currentLoads.data?.filter(l => l.status?.toLowerCase() === 'dispatched' || l.status?.toLowerCase() === 'in_transit').length || 0,
        cancelledLoads: currentLoads.data?.filter(l => l.status?.toLowerCase() === 'cancelled').length || 0,
        totalMiles,
        avgMilesPerLoad,
        avgDeliveryTime: 0, // Can be calculated if we have pickup/delivery timestamps
        topDrivers,
        topTrucks,
        topCustomers,
        topRoutes,
        topDispatchers,
        topCompanies,
        revenueByDay,
        loadsByStatus,
        revenueByState,
        totalMaintenanceCost,
        totalMaintenanceLogs,
        maintenanceThisMonth,
        driverMaintenanceCost,
        companyMaintenanceCost
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDateFilter(range: DateRange): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (range) {
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return weekAgo.toISOString();
      
      case 'current_month':
        // First day of current month
        return new Date(year, month, 1).toISOString();
      
      case 'previous_month':
        // First day of previous month
        return new Date(year, month - 1, 1).toISOString();
      
      case 'q1':
        // January 1
        return new Date(year, 0, 1).toISOString();
      
      case 'q2':
        // April 1
        return new Date(year, 3, 1).toISOString();
      
      case 'q3':
        // July 1
        return new Date(year, 6, 1).toISOString();
      
      case 'q4':
        // October 1
        return new Date(year, 9, 1).toISOString();
      
      case 'year':
        // January 1 of current year
        return new Date(year, 0, 1).toISOString();
    }
  }

  function getEndDateFilter(range: DateRange): string | null {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (range) {
      case 'previous_month':
        // Last day of previous month (first day of current month)
        return new Date(year, month, 1).toISOString();
      
      case 'q1':
        // March 31 (April 1)
        return new Date(year, 3, 1).toISOString();
      
      case 'q2':
        // June 30 (July 1)
        return new Date(year, 6, 1).toISOString();
      
      case 'q3':
        // September 30 (October 1)
        return new Date(year, 9, 1).toISOString();
      
      case 'q4':
        // December 31 (January 1 next year)
        return new Date(year + 1, 0, 1).toISOString();
      
      default:
        // No end date for current periods
        return null;
    }
  }

  function getPreviousDateFilter(range: DateRange): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    switch (range) {
      case 'week':
        const twoWeeksAgo = new Date(now);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        return twoWeeksAgo.toISOString();
      
      case 'current_month':
        // First day of previous month (for comparison)
        return new Date(year, month - 1, 1).toISOString();
      
      case 'previous_month':
        // First day of 2 months ago
        return new Date(year, month - 2, 1).toISOString();
      
      case 'q1':
        // Q1 of previous year
        return new Date(year - 1, 0, 1).toISOString();
      
      case 'q2':
        // Q2 of previous year
        return new Date(year - 1, 3, 1).toISOString();
      
      case 'q3':
        // Q3 of previous year
        return new Date(year - 1, 6, 1).toISOString();
      
      case 'q4':
        // Q4 of previous year
        return new Date(year - 1, 9, 1).toISOString();
      
      case 'year':
        // Previous year
        return new Date(year - 1, 0, 1).toISOString();
    }
  }

  function generateRevenueByDay(revenueRecords: any[], range: DateRange) {
    let days = 30; // default
    if (range === 'week') days = 7;
    else if (range === 'current_month' || range === 'previous_month') days = 30;
    else if (range === 'q1' || range === 'q2' || range === 'q3' || range === 'q4') days = 90;
    else if (range === 'year') days = 365;
    const data: Array<{ date: string; revenue: number; loads: number }> = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayRecords = revenueRecords.filter(r => {
        const recordDate = new Date(r.completed_date).toISOString().split('T')[0];
        return recordDate === dateStr;
      });
      
      data.push({
        date: dateStr,
        revenue: dayRecords.reduce((sum, r) => sum + Number(r.total_revenue), 0),
        loads: dayRecords.length
      });
    }
    
    return data;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading advanced analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Analytics Data Available</h3>
          <p className="text-yellow-700 mb-4">
            To see analytics, you need to have completed loads with revenue records.
          </p>
          <div className="text-sm text-yellow-600 space-y-2">
            <p>• Mark dispatches as "Delivered" in the Dispatches view</p>
            <p>• Revenue records are automatically created from delivered dispatches</p>
            <p>• Check the browser console (F12) for any errors</p>
          </div>
        </div>
      </div>
    );
  }

  // Chart configurations
  // Revenue Trend — bucketed bars. Monthly for year/quarters, daily for short
  // ranges, so the chart is never a noisy 365-point line.
  const revenueChartData = (() => {
    const byMonth = dateRange === 'year' || dateRange.startsWith('q');
    const buckets = new Map<string, number>();
    const order: string[] = [];
    analytics.revenueByDay.forEach(d => {
      const dt = new Date(d.date);
      const key = byMonth
        ? dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!buckets.has(key)) { buckets.set(key, 0); order.push(key); }
      buckets.set(key, (buckets.get(key) || 0) + d.revenue);
    });
    return {
      labels: order,
      datasets: [{
        label: 'Revenue',
        data: order.map(k => buckets.get(k) || 0),
        backgroundColor: (ctx: any) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return 'rgba(37,99,235,0.8)';
          const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          g.addColorStop(0, 'rgba(37,99,235,0.45)');
          g.addColorStop(1, 'rgba(37,99,235,1)');
          return g;
        },
        hoverBackgroundColor: 'rgba(29,78,216,1)',
        borderRadius: 8,
        maxBarThickness: 46,
      }],
    };
  })();

  // Revenue share per dispatcher (top 8 + "Others"), pie chart.
  const DISPATCHER_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
  ];
  const dispatcherChartData = (() => {
    const sorted = analytics.topDispatchers
      .filter(d => d.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);
    const TOP = 8;
    const top = sorted.slice(0, TOP);
    const restTotal = sorted.slice(TOP).reduce((s, d) => s + d.revenue, 0);
    const labels = top.map(d => d.name);
    const data = top.map(d => d.revenue);
    const colors = top.map((_, i) => DISPATCHER_COLORS[i % DISPATCHER_COLORS.length]);
    if (restTotal > 0) {
      labels.push('Others');
      data.push(restTotal);
      colors.push('#cbd5e1');
    }
    return {
      labels,
      datasets: [{ data, backgroundColor: colors, borderColor: '#ffffff', borderWidth: 2 }],
    };
  })();
  const dispatcherTotal = dispatcherChartData.datasets[0].data.reduce((s, v) => s + v, 0);

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Advanced Analytics</h1>
          <p className="text-slate-600 mt-1">Comprehensive insights across all operations</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">This Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="q1">Q1 (Jan-Mar)</option>
            <option value="q2">Q2 (Apr-Jun)</option>
            <option value="q3">Q3 (Jul-Sep)</option>
            <option value="q4">Q4 (Oct-Dec)</option>
            <option value="year">Year to Date</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
            <Download className="w-4 h-4" />
            Export
          </button>
          <div className="text-xs text-slate-500 bg-slate-100 px-3 py-2 rounded-lg flex items-center gap-2">
            <Activity className="w-3 h-3 animate-pulse text-green-600" />
            Auto-refreshing every 5 min
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'overview'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            📊 Overview
          </button>
          <button
            onClick={() => setActiveTab('trucks')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'trucks'
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            🚛 Truck Performance
          </button>
          <button
            onClick={() => setActiveTab('drivers')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'drivers'
                ? 'bg-green-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            👤 Driver Performance
          </button>
          <button
            onClick={() => setActiveTab('dispatchers')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'dispatchers'
                ? 'bg-orange-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            📋 Dispatcher Performance
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'companies'
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            🏢 Company Performance
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard
          title="Total Revenue"
          value={`$${analytics.totalRevenue.toLocaleString()}`}
          change={analytics.revenueChange}
          icon={DollarSign}
          color="blue"
        />
        <MetricCard
          title="Completed Loads"
          value={analytics.completedLoads.toString()}
          subtitle={`${analytics.activeLoads} active`}
          icon={Package}
          color="green"
        />
        <MetricCard
          title="Total Miles"
          value={analytics.totalMiles.toLocaleString()}
          subtitle={`${analytics.avgMilesPerLoad.toFixed(0)} avg/load (loaded + empty)`}
          icon={MapPin}
          color="purple"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-600">Avg Revenue/Load</p>
            <BarChart3 className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900">${analytics.avgRevenuePerLoad.toFixed(0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-600">Revenue/Mile</p>
            <Activity className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900">${analytics.revenuePerMile.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-600">Avg Miles/Load</p>
            <Truck className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{analytics.avgMilesPerLoad.toFixed(0)}</p>
        </div>
      </div>

      {/* Maintenance Metrics */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-blue-600" />
          Maintenance Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Wrench className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Logs</p>
                <p className="text-2xl font-bold text-slate-900">{analytics.totalMaintenanceLogs}</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-2xl font-bold text-slate-900">${analytics.maintenanceThisMonth.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Driver Cost</p>
                <p className="text-2xl font-bold text-slate-900">${analytics.driverMaintenanceCost.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Company Cost</p>
                <p className="text-2xl font-bold text-slate-900">${analytics.companyMaintenanceCost.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">${analytics.totalMaintenanceCost.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Revenue Trend
            </h3>
            <span className="text-xs font-medium text-slate-400">{dateRange === 'year' ? 'Year to date' : 'Selected period'}</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-4">${analytics.totalRevenue.toLocaleString()}</p>
          <div className="h-[280px]">
            <Bar
              data={revenueChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    backgroundColor: '#0f172a',
                    padding: 12,
                    cornerRadius: 8,
                    displayColors: false,
                    callbacks: {
                      label: (ctx: any) => ` $${Number(ctx.raw).toLocaleString()}`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (v: any) => {
                        const n = Number(v);
                        return n >= 1000 ? '$' + (n / 1000).toFixed(0) + 'k' : '$' + n;
                      },
                      color: '#94a3b8',
                    },
                    grid: { color: '#f1f5f9' },
                    border: { display: false },
                  },
                  x: {
                    ticks: { color: '#94a3b8' },
                    grid: { display: false },
                    border: { display: false },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Revenue by Dispatcher */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Revenue by Dispatcher
            </h3>
            <span className="text-xs font-medium text-slate-400">{dispatcherChartData.labels.length} shown</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mb-4">${dispatcherTotal.toLocaleString()}</p>
          {dispatcherTotal > 0 ? (
            <div className="h-[280px]">
              <Pie
                data={dispatcherChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right' as const,
                      labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 }, padding: 12 },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx: any) => {
                          const val = Number(ctx.raw);
                          const pct = dispatcherTotal > 0 ? ((val / dispatcherTotal) * 100).toFixed(1) : '0';
                          return ` ${ctx.label}: $${val.toLocaleString()} (${pct}%)`;
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="h-[280px] flex flex-col items-center justify-center text-slate-400">
              <Users className="w-12 h-12 mb-2 text-slate-300" />
              <p className="text-sm">No dispatcher revenue in this period</p>
            </div>
          )}
        </div>
      </div>

      {/* Top 5 Quick Stats */}
      <div className="grid grid-cols-4 gap-6">
        {/* Top 5 Drivers - Compact Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              {rankLabel(rankModes.drivers)} Drivers
            </h3>
            <RankToggle section="drivers" />
          </div>
          <div className="space-y-3">
            {analytics.topDrivers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No driver data available</p>
                <p className="text-xs mt-1">Complete deliveries to see driver performance</p>
              </div>
            ) : (
              pickFive(analytics.topDrivers, rankModes.drivers).map((driver, idx) => (
              <div key={driver.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-transparent rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{driver.name}</p>
                    <p className="text-xs text-slate-500">{driver.loads} loads • {driver.miles.toLocaleString()} mi</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600">${driver.revenue.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">${driver.avgRevenue.toFixed(0)}/load</p>
                </div>
              </div>
              ))
            )}
          </div>
        </div>

        {/* Top 5 Trucks - Compact Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-600" />
              {rankLabel(rankModes.trucks)} Trucks
            </h3>
            <RankToggle section="trucks" />
          </div>
          <div className="space-y-3">
            {analytics.topTrucks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Truck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No truck data available</p>
                <p className="text-xs mt-1">Complete deliveries to see truck performance</p>
              </div>
            ) : (
              pickFive(analytics.topTrucks, rankModes.trucks).map((truck, idx) => (
                <div key={truck.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-transparent rounded-lg border border-purple-100">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-bold">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{truck.unitNumber}</p>
                      <p className="text-xs text-slate-500">{truck.loads} loads • {truck.miles.toLocaleString()} mi</p>
                      {truck.maintenanceCost > 0 && (
                        <p className="text-xs text-red-600">Maint: -${truck.maintenanceCost.toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-600">${truck.revenue.toLocaleString()}</p>
                    {truck.maintenanceCost > 0 && (
                      <p className="text-xs text-green-600">Net: ${truck.netProfit.toLocaleString()}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top 5 Pickup States - Compact Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-600" />
              {rankLabel(rankModes.states)} Pickup States
            </h3>
            <RankToggle section="states" />
          </div>
          <div className="space-y-3">
            {pickFive(analytics.revenueByState, rankModes.states).map((state, idx) => (
              <div key={state.state} className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-transparent rounded-lg border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold">
                    {idx + 1}
                  </div>
                  <p className="font-semibold text-slate-900 text-sm">{state.state}</p>
                </div>
                <p className="text-lg font-bold text-green-600">${(state.revenue / 1000).toFixed(1)}K</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top 5 Dispatchers - Compact Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              {rankLabel(rankModes.dispatchers)} Dispatchers
            </h3>
            <RankToggle section="dispatchers" />
          </div>
          <div className="space-y-3">
            {analytics.topDispatchers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No dispatcher data available</p>
                <p className="text-xs mt-1">Complete deliveries to see dispatcher performance</p>
              </div>
            ) : (
              pickFive(analytics.topDispatchers, rankModes.dispatchers).map((dispatcher, idx) => (
              <div key={dispatcher.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-transparent rounded-lg border border-orange-100">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-600 text-white text-sm font-bold">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{dispatcher.name}</p>
                    <p className="text-xs text-slate-500">{dispatcher.loads} loads • {dispatcher.miles.toLocaleString()} mi</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-orange-600">${dispatcher.revenuePerMile.toFixed(2)}/mi</p>
                  <p className="text-xs text-slate-500">${dispatcher.revenue.toLocaleString()} total</p>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top Drivers Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">{rankLabel(rankModes.tblDrivers, 10)} Driver Performance</h3>
            <RankToggle section="tblDrivers" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-xs font-semibold text-slate-600">Driver</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Revenue</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Loads</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Miles</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Avg/Load</th>
                </tr>
              </thead>
              <tbody>
                {pickN(analytics.topDrivers, rankModes.tblDrivers, 10).map((driver, idx) => (
                  <tr key={driver.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400">#{idx + 1}</span>
                        <span className="text-sm font-medium text-slate-900">{driver.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-2 text-sm font-semibold text-green-600">
                      ${driver.revenue.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-2 text-sm text-slate-600">{driver.loads}</td>
                    <td className="text-right py-3 px-2 text-sm text-slate-600">{driver.miles.toLocaleString()}</td>
                    <td className="text-right py-3 px-2 text-sm text-slate-600">${driver.avgRevenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Trucks Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">{rankLabel(rankModes.tblTrucks, 10)} Truck Performance</h3>
            <RankToggle section="tblTrucks" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-xs font-semibold text-slate-600">Truck</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Revenue</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Loads</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Miles</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Util.</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Maint.</th>
                  <th className="text-right py-3 px-2 text-xs font-semibold text-slate-600">Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {pickN(analytics.topTrucks, rankModes.tblTrucks, 10).map((truck, idx) => (
                  <tr key={truck.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400">#{idx + 1}</span>
                        <span className="text-sm font-medium text-slate-900">{truck.unitNumber}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-2 text-sm font-semibold text-green-600">
                      ${truck.revenue.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-2 text-sm text-slate-600">{truck.loads}</td>
                    <td className="text-right py-3 px-2 text-sm text-slate-600">{truck.miles.toLocaleString()}</td>
                    <td className="text-right py-3 px-2 text-sm text-blue-600">{truck.utilization}</td>
                    <td className="text-right py-3 px-2 text-sm text-red-600">
                      -${truck.maintenanceCost.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-2">
                      <span className={`text-sm font-semibold ${truck.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${truck.netProfit.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top Customers & Routes */}
      <div className="grid grid-cols-2 gap-6">
        {/* Top Customers */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">{rankLabel(rankModes.tblCustomers, 10)} Customers</h3>
            <RankToggle section="tblCustomers" />
          </div>
          <div className="space-y-3">
            {pickN(analytics.topCustomers, rankModes.tblCustomers, 10).map((customer, idx) => (
              <div key={customer.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-400">#{idx + 1}</span>
                  <div>
                    <p className="font-medium text-slate-900">{customer.name}</p>
                    <p className="text-xs text-slate-500">{customer.loads} loads • ${customer.avgRate.toFixed(0)}/load avg</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-green-600">${customer.revenue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Routes */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">{rankLabel(rankModes.tblRoutes, 10)} Routes</h3>
            <RankToggle section="tblRoutes" />
          </div>
          <div className="space-y-3">
            {pickN(analytics.topRoutes, rankModes.tblRoutes, 10).map((route, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-400">#{idx + 1}</span>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{route.route}</p>
                    <p className="text-xs text-slate-500">{route.loads} loads • ${route.avgRate.toFixed(0)}/load avg</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-blue-600">${route.revenue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      )}

      {/* Truck Performance Tab */}
      {activeTab === 'trucks' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">🚛 Truck Performance</h2>
              <p className="text-slate-600">Showing {analytics.topTrucks.filter(t => 
                t.unitNumber.toLowerCase().includes(truckSearch.toLowerCase())
              ).length} trucks</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search trucks..."
                  value={truckSearch}
                  onChange={(e) => setTruckSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-64"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="revenue">Sort by Revenue</option>
                <option value="loads">Sort by Loads</option>
                <option value="miles">Sort by Miles</option>
                <option value="efficiency">Sort by $/Mile</option>
              </select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Truck #</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Loads</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Miles</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Maintenance</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Net Profit</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Mile</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topTrucks
                  .filter(truck => truck.unitNumber.toLowerCase().includes(truckSearch.toLowerCase()))
                  .sort((a, b) => {
                    switch(sortBy) {
                      case 'loads': return b.loads - a.loads;
                      case 'miles': return b.miles - a.miles;
                      case 'efficiency': return (b.revenue / b.miles) - (a.revenue / a.miles);
                      default: return b.revenue - a.revenue;
                    }
                  })
                  .map((truck, idx) => (
                  <tr key={truck.id} className="border-b border-slate-100 hover:bg-purple-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-semibold text-slate-900">{truck.unitNumber}</span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-lg font-bold text-purple-600">${truck.revenue.toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{truck.loads}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{truck.miles.toLocaleString()}</td>
                    <td className="text-right py-4 px-4">
                      <span className="text-red-600 font-semibold">-${truck.maintenanceCost.toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className={`font-bold ${truck.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${truck.netProfit.toLocaleString()}
                      </span>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-blue-600 font-semibold">${truck.revenuePerMile.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Driver Performance Tab */}
      {activeTab === 'drivers' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">👤 Driver Performance</h2>
              <p className="text-slate-600">Showing {analytics.topDrivers.filter(d => 
                d.name.toLowerCase().includes(driverSearch.toLowerCase())
              ).length} drivers</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 w-64"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="revenue">Sort by Revenue</option>
                <option value="loads">Sort by Loads</option>
                <option value="miles">Sort by Miles</option>
                <option value="efficiency">Sort by $/Mile</option>
              </select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Driver</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">
                    <div className="flex items-center justify-end gap-1">
                      Company Revenue
                      <span className="text-xs text-slate-400" title="Total revenue from loads (not driver pay)">ⓘ</span>
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Loads</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Miles</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Load</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Rate/Mile</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">
                    <div className="flex flex-col items-center">
                      <span>Inspections</span>
                      <span className="text-xs font-normal text-slate-500">(Pass/Fail)</span>
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Accidents</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topDrivers
                  .filter(driver => driver.name.toLowerCase().includes(driverSearch.toLowerCase()))
                  .sort((a, b) => {
                    switch(sortBy) {
                      case 'loads': return b.loads - a.loads;
                      case 'miles': return b.miles - a.miles;
                      case 'efficiency': return (b.revenue / b.miles) - (a.revenue / a.miles);
                      default: return b.revenue - a.revenue;
                    }
                  })
                  .map((driver, idx) => (
                  <tr key={driver.id} className="border-b border-slate-100 hover:bg-green-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-semibold text-slate-900">{driver.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-lg font-bold text-green-600">${driver.revenue.toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{driver.loads}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{driver.miles.toLocaleString()}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">${driver.avgRevenue.toFixed(0)}</td>
                    <td className="text-right py-4 px-4">
                      <span className="text-blue-600 font-semibold">${(driver.revenue / driver.miles).toFixed(2)}</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ {driver.inspectionsPassed}
                        </span>
                        {driver.inspectionsFailed > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ✗ {driver.inspectionsFailed}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center py-4 px-4">
                      {driver.accidents > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {driver.accidents}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dispatcher Performance Tab */}
      {activeTab === 'dispatchers' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">📋 Dispatcher Performance</h2>
              <p className="text-slate-600">Showing {analytics.topDispatchers.filter(d => 
                d.name.toLowerCase().includes(dispatcherSearch.toLowerCase())
              ).length} dispatchers</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search dispatchers..."
                  value={dispatcherSearch}
                  onChange={(e) => setDispatcherSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 w-64"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="revenue">Sort by Revenue</option>
                <option value="cut">Sort by Cut</option>
                <option value="loads">Sort by Loads</option>
                <option value="miles">Sort by Miles</option>
                <option value="efficiency">Sort by $/Mile</option>
              </select>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Dispatcher</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Cut</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Loads</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Miles</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Load</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Mile</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topDispatchers
                  .filter(dispatcher => dispatcher.name.toLowerCase().includes(dispatcherSearch.toLowerCase()))
                  .sort((a, b) => {
                    switch(sortBy) {
                      case 'cut': return b.cut - a.cut;
                      case 'loads': return b.loads - a.loads;
                      case 'miles': return b.miles - a.miles;
                      case 'efficiency': return b.revenuePerMile - a.revenuePerMile;
                      default: return b.revenue - a.revenue;
                    }
                  })
                  .map((dispatcher, idx) => (
                  <tr key={dispatcher.id} className="border-b border-slate-100 hover:bg-orange-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-600 text-white text-sm font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-semibold text-slate-900">{dispatcher.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-lg font-bold text-orange-600">${dispatcher.revenue.toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="font-bold text-emerald-600">${Math.round(dispatcher.cut).toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{dispatcher.loads}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{dispatcher.miles.toLocaleString()}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">${dispatcher.avgRevenue.toFixed(0)}</td>
                    <td className="text-right py-4 px-4">
                      <span className="text-purple-600 font-semibold">${dispatcher.revenuePerMile.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Company Performance Tab */}
      {activeTab === 'companies' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">🏢 Company Performance</h2>
              <p className="text-slate-600">Showing {analytics.topCompanies.length} companies</p>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Company</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Loads</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Miles</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Load</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">$/Mile</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topCompanies.map((company, idx) => (
                  <tr key={company.id} className="border-b border-slate-100 hover:bg-indigo-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-semibold text-slate-900">{company.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className="text-lg font-bold text-indigo-600">${company.revenue.toLocaleString()}</span>
                    </td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{company.loads}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">{company.miles.toLocaleString()}</td>
                    <td className="text-right py-4 px-4 text-slate-700 font-medium">${company.avgRevenue.toFixed(0)}</td>
                    <td className="text-right py-4 px-4">
                      <span className="text-purple-600 font-semibold">${company.revenuePerMile.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  change?: number;
  subtitle?: string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

function MetricCard({ title, value, change, subtitle, icon: Icon, color }: MetricCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-semibold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-600 mt-1">{subtitle || title}</p>
    </div>
  );
}
